import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import HeroSection from "./components/HeroSection";
import TechnologyPartners from "./components/TechnologyPartners";
import TrustedClients from "./components/TrustedClients";
import ClientTestimonials from "./components/ClientTestimonials";
import CTASection from "./components/CTASection";
import ServicesPreview from "./components/ServicesPreview";
import SSMProductSection from "./components/SSMProductSection";

gsap.registerPlugin(ScrollTrigger);


const BANNER_IMAGES = [
  "/assets/physical/1.png",
  "/assets/physical/2.png",
  "/assets/physical/3.png",
  "/assets/physical/4.png",
  "/assets/physical/5.png",
  "/assets/physical/6.png",
  "/assets/physical/7.png",
  "/assets/physical/8.png",
  "/assets/physical/9.png",
  "/assets/physical/10.png",
];

const Homepage = () => {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const imageRefs = useRef([]);
  const ctx = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check if mobile on mount and on resize
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Clean up any existing animations
    if (ctx.current) {
      ctx.current.revert();
    }
    
    // Kill any ScrollTriggers associated with this section
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.trigger === sectionRef.current) {
        trigger.kill();
      }
    });

    ctx.current = gsap.context(() => {
      const images = imageRefs.current.filter(Boolean);
      if (!images.length || !sectionRef.current || !stickyRef.current) return;

      if (isMobile) {
        // MOBILE-FRIENDLY GSAP ANIMATION
        // Simple fade-in effect for mobile
        gsap.set(images, {
          opacity: 0,
          y: 30,
          scale: 0.95
        });

        // Create a scroll-triggered sequence
        images.forEach((img, index) => {
          gsap.to(img, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.5,
            ease: "power2.out",
            scrollTrigger: {
              trigger: img,
              start: "top 80%",
              end: "top 50%",
              toggleActions: "play none none reverse",
              markers: false // Set to true for debugging
            }
          });
        });

        // Simple parallax effect for the entire section
        gsap.to(sectionRef.current, {
          y: 50,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1
          }
        });

      } else {
        // DESKTOP GSAP ANIMATION
        // Pin the section
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top top",
          end: `+=${BANNER_IMAGES.length * 100}vh`,
          pin: stickyRef.current,
          pinSpacing: true,
          anticipatePin: 1,
        });

        // Initial stacking setup
        gsap.set(images, {
          scale: (i) => 0.4 + i * 0.06,
          opacity: (i) => 0.2 + i * 0.16,
          y: (i) => 80 - i * 12,
          rotationY: (i) => 20 - i * 3,
          zIndex: (i) => i + 1,
        });

        // Create timeline for desktop animation
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: `+=${BANNER_IMAGES.length * 100}vh`,
            scrub: 1,
          },
        });

        images.forEach((img, i) => {
          const progress = i * 0.3;
          tl.to(
            img,
            {
              scale: 1.02,
              opacity: 1,
              y: 0,
              rotationY: 0,
              zIndex: 50,
              duration: 0.4,
            },
            progress
          ).to(
            img,
            {
              scale: 0.98,
              opacity: 0.92,
              y: 5,
              rotationY: -1,
              zIndex: 20 + i,
              duration: 0.35,
            },
            progress + 0.4
          );
        });
      }
    }, sectionRef);

    return () => {
      if (ctx.current) {
        ctx.current.revert();
      }
    };
  }, [isMobile]);

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-hidden">
      <Header />

      <main className="flex-1">
        <HeroSection />

        {/* ================== PHYSICAL BRANDING SECTION ================== */}
        <section
          ref={sectionRef}
          className={`relative overflow-hidden bg-gradient-to-br from-[#2642B2] via-indigo-900 to-slate-900 text-white ${
            isMobile ? "py-12" : ""
          }`}
          style={{
            minHeight: isMobile ? "auto" : `${BANNER_IMAGES.length * 28}vh`,
          }}
        >
          {/* Bubble Background Effects */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 backdrop-blur-xl rounded-2xl animate-float-slow"></div>
            <div className="absolute top-1/2 right-20 w-16 h-16 bg-cyan-400/10 backdrop-blur-xl rounded-xl animate-float"></div>
            <div className="absolute bottom-24 left-1/3 w-24 h-24 bg-purple-500/10 backdrop-blur-xl rounded-2xl animate-float-slow delay-1000"></div>
            <div className="absolute bottom-20 right-10 w-20 h-20 bg-emerald-400/10 backdrop-blur-xl rounded-xl animate-float delay-2000"></div>
          </div>

          <div
            ref={stickyRef}
            className={`${isMobile ? "relative" : "sticky top-0"} ${
              isMobile ? "min-h-[80vh]" : "h-screen"
            }`}
          >
            <div className="h-full container mx-auto px-4 md:px-6 lg:px-8">
              <div className={`h-full ${
                isMobile 
                  ? "flex flex-col gap-8" 
                  : "grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center"
              }`}>
                
                {/* LEFT - TEXT CONTENT */}
                <div className={`w-full ${
                  isMobile 
                    ? "order-1 py-4" 
                    : "h-full flex flex-col justify-center py-8 lg:py-12 px-4 lg:px-6 z-40"
                }`}>
                  <div className="flex items-center gap-4 mb-6 lg:mb-8">
                    <div className="h-px w-8 lg:w-12 bg-gradient-to-r from-cyan-500 to-emerald-500" />
                    <span className="text-xs lg:text-sm font-semibold tracking-wider text-gray-300 uppercase">
                      Physical Branding
                    </span>
                  </div>

                  <div className="space-y-4 lg:space-y-6 max-w-lg">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                      <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-blue-100">
                        Corporate
                      </span>
                      <span className="block mt-2 lg:mt-3 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400">
                        Events &
                      </span>
                      <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-blue-100">
                        Physical
                      </span>
                      <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400">
                        Branding
                      </span>
                    </h2>

                    <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed">
                      We design, execute, and manage impactful on-ground branding
                      experiences that create real-world visibility and lasting
                      brand presence through immersive corporate events and
                      physical installations.
                    </p>
                  </div>
                </div>

                {/* RIGHT - IMAGE STACK */}
                <div className={`${
                  isMobile 
                    ? "order-2 w-full h-auto min-h-[50vh]" 
                    : "w-full h-[45vh] sm:h-[60vh] lg:h-[70vh] xl:h-[75vh] relative px-4 lg:px-8"
                }`}>
                  <div className={`${
                    isMobile 
                      ? "space-y-6" 
                      : "w-full h-full flex items-center justify-center"
                  }`}>
                    {BANNER_IMAGES.map((img, index) => (
                      <div
                        key={index}
                        ref={(el) => (imageRefs.current[index] = el)}
                        className={`${
                          isMobile 
                            ? "w-full rounded-2xl shadow-xl overflow-hidden" 
                            : "absolute inset-0 flex items-center justify-center"
                        }`}
                      >
                        <div className={isMobile ? "relative" : "w-[90vw] sm:w-[85vw] md:w-[80vw] lg:w-[90%] xl:w-[85%] max-w-[1000px] h-full rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden"}>
                          {!isMobile && (
                            <div className="absolute -inset-4 sm:-inset-6 lg:-inset-8 bg-gradient-to-r from-cyan-400/10 via-blue-400/10 to-purple-400/10 rounded-[32px] lg:rounded-[48px] blur-xl lg:blur-2xl" />
                          )}
                          {isMobile && (
                            <div className="absolute -inset-4 bg-gradient-to-r from-cyan-400/10 via-blue-400/10 to-purple-400/10 rounded-[24px] blur-lg" />
                          )}
                          <img
                            src={img}
                            alt={`Physical Branding Experience ${index + 1}`}
                            className={`${
                              isMobile 
                                ? "relative w-full h-48 sm:h-56 object-cover rounded-xl" 
                                : "w-full h-full object-cover brightness-110"
                            }`}
                            loading="lazy"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* ================== REST OF THE PAGE ================== */}
        <ServicesPreview />
        <SSMProductSection />
        <TechnologyPartners />
        <TrustedClients />
        <ClientTestimonials />
        <CTASection />

      </main>

      <Footer />

      {/* Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(1deg); }
        }

        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(-1deg); }
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 7s ease-in-out infinite;
        }

        .delay-1000 { animation-delay: 1s; }
        .delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default Homepage;